import type React from 'react';
import { createContext, useContext, useCallback, useMemo } from 'react';
import type { FormField, FormValues, FormFieldValue } from './types';

interface FormContextValue {
  values: FormValues;
  onChange: (values: FormValues) => void;
}

interface FieldContextValue {
  field: FormField;
  value: FormFieldValue;
  onChange: (value: FormFieldValue) => void;
}

const FormContext = createContext<FormContextValue | null>(null);
const FieldContext = createContext<FieldContextValue | null>(null);

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};

export const useFieldContext = () => {
  const context = useContext(FieldContext);
  if (!context) {
    throw new Error('useFieldContext must be used within a FieldProvider');
  }
  return context;
};

interface FormProviderProps {
  children: React.ReactNode;
  values: FormValues;
  onChange: (values: FormValues) => void;
}

export const FormProvider: React.FC<FormProviderProps> = ({
  children,
  values,
  onChange,
}) => {
  const handleFieldChange = useCallback((values: FormValues) => {
    onChange(values);
  }, [onChange]);

  const contextValue = useMemo(() => ({
    values,
    onChange: handleFieldChange,
  }), [values, handleFieldChange]);

  return (
    <FormContext.Provider value={contextValue}>
      {children}
    </FormContext.Provider>
  );
};

interface FieldProviderProps {
  children: React.ReactNode;
  field: FormField;
}

export const FieldProvider: React.FC<FieldProviderProps> = ({
  children,
  field,
}) => {
  const { values, onChange } = useFormContext();
  
  const handleChange = useCallback((value: FormFieldValue) => {
    onChange({
      ...values,
      [field.name]: value,
    });
  }, [values, onChange, field.name]);

  const value = values[field.name];

  const contextValue = useMemo(() => ({
    field,
    value,
    onChange: handleChange,
  }), [field, value, handleChange]);

  return (
    <FieldContext.Provider value={contextValue}>
      {children}
    </FieldContext.Provider>
  );
};
